import { useLoaderData } from 'react-router'
import { observer } from 'mobx-react-lite'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { useDocumentTitle } from '@/hooks/document-title'
import { useActivation } from '@/hooks/view-model-hooks'
import { match } from '@/utils/pattern-match-util'
import {
  ClientUserDetails,
  ClientUserDetailsHeader,
  ClientUserDetailsTabs,
} from '@/features/client-user-management/ClientUserDetails'
import { ClientUserDetailsInformationContainer } from '@/features/client-user-management/components/ClientUserDetailsInformation/ClientUserDetailsInformationContainer'
import { DocumentsTabContainer } from '@/features/documents/components/DocumentsTab/DocumentsTabContainer'
import { UserJobsTabContainer } from '@/features/jobs/list/components/UserJobsTab/UserJobsTabContainer'
import type { ClientUserDetailsViewLoaderData } from '@/features/client-user-management/client-user-management-route-loaders'
import type { ClientUserDetailsViewModel } from './ClientUserDetailsViewModel'
import { AdvisorClientsTabContainer } from './AdvisorClientsTabContainer'

export default observer(function ClientUserDetailsContainer() {
  // The route loader provides the view model for the route's client.
  const data = useLoaderData<ClientUserDetailsViewLoaderData>()
  useDocumentTitle('User Details')

  return data && <ClientUserDetailsViewContainer vm={data.vm} />
})

interface ClientUserDetailsViewContainerProps {
  vm: ClientUserDetailsViewModel
}

const ClientUserDetailsViewContainer = observer(
  function ClientUserDetailsViewContainer({
    vm,
  }: ClientUserDetailsViewContainerProps) {
    useActivation(vm)

    return vm.fetch.match({
      pending: () => <ContentLoading />,
      rejected: (err) => <ContentError error={err} />,
      resolved: () =>
        !vm.hasMember ? (
          <ContentLoading />
        ) : (
          <ClientUserDetails
            header={
              <ClientUserDetailsHeader
                avatar={vm.member.avatar}
                displayName={vm.member.displayName}
                email={vm.member.email}
                canCreateJob={vm.canCreateJobForUser}
                onCreateJob={vm.createJob}
              />
            }
            tabs={
              <ClientUserDetailsTabs
                tabs={vm.tabs}
                currentTab={vm.currentTab}
                onTabChange={vm.setTab}
              />
            }
            information={<ClientUserDetailsInformationContainer vm={vm} />}
            tabContent={match(vm.currentTab, {
              Jobs: () => <UserJobsTabContainer vm={vm.jobsTabVm} />,
              Documents: () => <DocumentsTabContainer vm={vm.documentsTabVm} />,
              Clients: () => (
                <AdvisorClientsTabContainer vm={vm.clientsTabVm} />
              ),
            })}
          />
        ),
    })
  },
)
